import React from "react";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import {
  DriveItem,
  DragState,
  EditState,
  FileActionsConfig,
  SelectionState,
  SortState,
} from "../types";
import { getIconByMime } from "../helpers";
import SortableHeader from "./SortableHeader";
import FileActionsMenu from "./FileActionsMenu";

interface FileListViewProps {
  /** Rows to render (already sorted/sliced by the caller). */
  rows: DriveItem[];
  /** Whether to cap the height and scroll (more than one page of items). */
  scroll: boolean;
  sort: SortState;
  onItemDoubleClick: (item: DriveItem) => void;
  actions: FileActionsConfig;
  /** Type / size / date cell text — supplied per view. */
  getType: (item: DriveItem) => string;
  getSize: (item: DriveItem) => string;
  getDate: (item: DriveItem) => string;
  dateLabel?: string;
  /** Truncate long names to N chars (My Folder list only). */
  truncateNameAt?: number;
  edit?: EditState;
  /** Selection + drag enable the interactive My Folder behaviour. */
  selection?: SelectionState;
  drag?: DragState;
  showSharedBy?: boolean;
  renderSharedBy?: (item: DriveItem) => React.ReactNode;
}

/** Tabular file listing shared by the My Folder, Shared and Official views. */
const FileListView = ({
  rows,
  scroll,
  sort,
  onItemDoubleClick,
  actions,
  getType,
  getSize,
  getDate,
  dateLabel = "Date",
  truncateNameAt,
  edit,
  selection,
  drag,
  showSharedBy,
  renderSharedBy,
}: FileListViewProps) => {
  const displayName = (item: DriveItem) =>
    truncateNameAt && item.name.length > truncateNameAt
      ? item.name.slice(0, truncateNameAt) + "..."
      : item.name;

  return (
    <div
      style={{
        maxHeight: scroll ? "700px" : "none",
        overflowY: scroll ? "auto" : "visible",
        paddingRight: "8px",
        position: "relative",
      }}
    >
      <table className="table table-hover">
        <thead className="bg-light">
          <tr>
            <SortableHeader label="Name" column="name" sort={sort} className="file-name" />
            <SortableHeader label="Type" column="type" sort={sort} className="file-type" />
            <SortableHeader label="Size" column="size" sort={sort} className="file-size" />
            <SortableHeader label={dateLabel} column="date" sort={sort} className="file-date" />
            {showSharedBy && <th className="file-sharedBy">Shared By</th>}
            <th className="file-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const rowProps: React.HTMLAttributes<HTMLTableRowElement> & {
              draggable?: boolean;
            } = { onDoubleClick: () => onItemDoubleClick(item) };

            if (selection && drag) {
              const isSelected = selection.selectedItems.some(
                (sel) => sel.id === item.id,
              );
              rowProps.onClick = (e) => selection.onItemClick(item, e);
              rowProps.onContextMenu = (e) => selection.onContextMenu(e, item);
              rowProps.className = isSelected
                ? "table-active"
                : "" + (drag.dragOverItem === item.id ? " drag-over" : "");
              rowProps.draggable = true;
              rowProps.onDragStart = (e) => drag.onDragStart(e, item);
              rowProps.onDragOver = (e) => drag.onDragOver(e, item);
              rowProps.onDragLeave = drag.onDragLeave;
              rowProps.onDrop = (e) => drag.onDrop(e, item);
              rowProps.style = {
                cursor: "grab",
                opacity: drag.isDragging && isSelected ? 0.5 : 1,
              };
            }

            return (
              <tr key={item.id} {...rowProps}>
                <td className="file-name">
                  <ImageWithBasePath
                    src={`assets/img/icons/${getIconByMime(item)}`}
                    alt={item.name}
                    width={18}
                    height={18}
                  />
                  {edit && edit.editingItemId === item.id ? (
                    <input
                      type="text"
                      className="form-control form-control-sm d-inline-block w-auto ms-2"
                      value={edit.editName}
                      onChange={(e) => edit.setEditName(e.target.value)}
                      onKeyDown={(e) => edit.onKeyDown(e, item)}
                      onBlur={() => edit.onBlur(item)}
                      autoFocus
                    />
                  ) : (
                    <span className="ms-2">{displayName(item)}</span>
                  )}
                </td>
                <td className="file-types">
                  {item.type === 1 ? "Folder" : getType(item)}
                </td>
                <td className="file-size">{getSize(item)}</td>
                <td className="file-date">{getDate(item)}</td>
                {showSharedBy && (
                  <td className="file-sharedBy">{renderSharedBy?.(item)}</td>
                )}
                <td className="file-actions">
                  <FileActionsMenu item={item} layout="list" actions={actions} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FileListView;
